import { type Element } from '@makeswift/controls'

import { type Action, type UnknownAction, type DocumentPayload, isKnownAction } from '../actions'

import { SharedActionTypes } from '../shared-api'

export {
  type Data,
  type ElementData,
  type ElementReference,
  type Element,
  isElementReference,
} from '@makeswift/controls'

export type DocumentReference = {
  key: string
}

export function createDocumentReference(key: string): DocumentReference {
  return { key }
}

type BaseDocument = {
  key: string
  rootElement: Element
  locale: string | null
}

export const EMBEDDED_DOCUMENT_TYPE = 'EMBEDDED_DOCUMENT' as const

export type EmbeddedDocument = {
  key: string
  locale: string | null
  id: string
  type: string
  name: string
  rootElement: Element
  meta: { allowLocaleFallback: boolean; requestedLocale: string | null; description?: string }
  __type: typeof EMBEDDED_DOCUMENT_TYPE
}

export type Document = BaseDocument | EmbeddedDocument

export function getRootElement(document: Document | DocumentPayload): Element {
  return document.rootElement
}

export function createBaseDocument(
  key: string,
  rootElement: Element,
  locale: string | null,
): Document {
  return { key, rootElement, locale }
}

export type State = Map<string, Document>

export function getInitialState({ documents = [] }: { documents?: Document[] } = {}): State {
  return new Map(documents.map(document => [document.key, document]))
}

export function getDocuments(state: State): State {
  return state
}

export function getDocument(state: State, documentKey: string): Document | null {
  return getDocuments(state).get(documentKey) ?? null
}

export function reducer(state: State = getInitialState(), action: Action | UnknownAction): State {
  if (!isKnownAction(action)) return state

  switch (action.type) {
    case SharedActionTypes.REGISTER_DOCUMENT:
      const { documentKey, document } = action.payload
      return new Map(state).set(documentKey, { ...document, locale: document.locale ?? null })

    case SharedActionTypes.UNREGISTER_DOCUMENT: {
      const nextState = new Map(state)

      const deleted = nextState.delete(action.payload.documentKey)

      return deleted ? nextState : state
    }

    default:
      return state
  }
}
